import SBDropdown from '@Components/Dropdown';
import { ShipmentKycType } from '@Reducers/Shipment/Type';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid, IconButton, Radio, Typography, Box, Paper, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { uploadFedExDocumentResponseType } from '@Reducers/AdminShipment/Type';
import { viewSignaleDocument } from '@Reducers/kyc/actions';
import { DocumentOptions } from './constants';
import SbTextField from '@Components/Textfield';
import DatePicker from '@Components/DatePicker';

const Wrapper = styled(Grid)`
  border-bottom: 1px solid;
  padding: 20px;
  &:last-child {
    border-bottom: none;
  }
`;

const StyleIconButton = styled(IconButton)(({ theme: _ }) => ({
  border: '1px solid #b2b2b2',
  padding: 8,
  borderRadius: 8,
  color: '#000',
  fontSize: '24px',
}));

const CommercialInvoicePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginTop: theme.spacing(2),
  backgroundColor: '#f8f9fa',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const CommercialInvoiceHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::before': {
    content: '""',
    display: 'block',
    width: 4,
    height: 20,
    backgroundColor: theme.palette.primary.main,
    borderRadius: 2,
  },
}));

interface SingleDocumentType {
  label: string;
  documentData: ShipmentKycType;
  required: boolean;
  modalView?: boolean;
  handleFileChecked: (data: ShipmentKycType) => void;
  selectedDocument: ShipmentKycType | null;
  setDocumentType: React.Dispatch<React.SetStateAction<string>>;
  documentType: string;
  setCommInvoiceNumber: React.Dispatch<React.SetStateAction<string>>;
  setCommInvoiceDate: Dispatch<SetStateAction<Dayjs | null>>;
  commInvoiceNumber: string;
  commInvoiceDate: Dayjs | null;
}

function SingleDocument(props: SingleDocumentType) {
  const {
    label,
    required,
    documentData,
    handleFileChecked,
    selectedDocument,
    setDocumentType,
    documentType,
    setCommInvoiceNumber,
    setCommInvoiceDate,
    commInvoiceNumber,
    commInvoiceDate,
  } = props;
  const { emiratesDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const { uplodedDocuments } = emiratesDetails;

  const dispatch = useDispatch<AppDispatch>();

  const onViewDocument = () => {
    dispatch(viewSignaleDocument({ documentId: documentData?._id, url: 'shipment' }));
  };

  const isUploadedDate: string =
    (uplodedDocuments ?? []).filter(
      (item: uploadFedExDocumentResponseType) => item?._documentId === documentData?._id,
    )[0]?.createdAt || '';

  const isUploadedDocument: string =
    (uplodedDocuments ?? []).filter(
      (item: uploadFedExDocumentResponseType) => item?._documentId === documentData?._id,
    )[0]?._uploadedBy?.name || '';

  const showCommercialInvoiceFields =
    selectedDocument?._id === documentData._id && documentType === 'CommercialInvoice';

  useEffect(() => {
    if (selectedDocument?._id === documentData._id) {
      const doc = uplodedDocuments.find((d: { _documentId: string }) => d._documentId === documentData._id);
      if (doc) {
        setDocumentType(doc.documentType);
        setCommInvoiceNumber(doc.commInvoiceNumber || '');
        setCommInvoiceDate(doc.commInvoiceDate ? dayjs(doc.commInvoiceDate) : null);
      }
    }
  }, [uplodedDocuments, selectedDocument, documentData._id, setDocumentType]);

  console.info('uplodedDocuments', uplodedDocuments);

  return (
    <Wrapper
      container
      item
      alignItems="flex-start"
      className={`border-grey`}
      sx={{
        ':hover': {
          bgcolor: 'primary.light',
          '#documentType': {
            bgcolor: 'primary.light',
          },
        },
        transition: 'background-color 0.2s ease',
      }}
    >
      {/* Main Document Row */}
      <Grid container item xs={12} spacing={2} alignItems="center">
        {/* Radio and Label */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Radio
              checked={Boolean(selectedDocument?._id === documentData._id)}
              onChange={() => handleFileChecked(documentData)}
            />
            <Typography className="md-text font-semibold">
              {label} {required ? '*' : ''}
            </Typography>
          </Box>
        </Grid>

        {/* Document Type Dropdown */}
        <Grid item xs={12} md={3}>
          <SBDropdown
            disabled={Boolean(selectedDocument?._id !== documentData._id)}
            id="documentType"
            required
            placeholder="Select"
            value={selectedDocument?._id === documentData._id ? [documentType] : []}
            options={DocumentOptions}
            onChange={(value: string[]) => {
              if (value?.length) {
                setDocumentType(value[0]);
                if (value[0] !== 'CommercialInvoice') {
                  setCommInvoiceNumber('');
                  setCommInvoiceDate(null);
                }
              }
            }}
          />
        </Grid>

        {/* Upload Info */}
        <Grid item xs={12} md={3}>
          {isUploadedDate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {dayjs(isUploadedDate).format('DD/MM/YY hh:mm:ss A')}
              </Typography>
              {isUploadedDocument && (
                <Typography variant="body2" color="text.secondary" className="capitalize">
                  By: {isUploadedDocument}
                </Typography>
              )}
            </Box>
          )}
        </Grid>

        {/* View Button */}
        <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {documentData && (
            <StyleIconButton onClick={onViewDocument}>
              <VisibilityIcon fontSize="inherit" />
            </StyleIconButton>
          )}
        </Grid>
      </Grid>

      {/* Commercial Invoice Fields - Animated Section */}
      <Fade in={showCommercialInvoiceFields}>
        <Grid item xs={12} sx={{ mt: showCommercialInvoiceFields ? 2 : 0 }}>
          {showCommercialInvoiceFields && (
            <CommercialInvoicePaper elevation={0}>
              <CommercialInvoiceHeader variant="subtitle1">Commercial Invoice Details</CommercialInvoiceHeader>

              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <SbTextField
                    label="Commercial Invoice Number"
                    value={commInvoiceNumber}
                    placeholder="Enter Invoice Number"
                    onChange={e => setCommInvoiceNumber(e.target.value)}
                    error={!commInvoiceNumber ? 'Required field' : ''}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    required
                    label="Commercial Invoice Date"
                    value={commInvoiceDate || null}
                    inputFormat={'YYYY-MM-DD'}
                    handleChange={e => setCommInvoiceDate(e)}
                    error={!commInvoiceDate ? 'Required field' : ''}
                  />
                </Grid>
              </Grid>
            </CommercialInvoicePaper>
          )}
        </Grid>
      </Fade>
    </Wrapper>
  );
}

export default SingleDocument;
